import React, { useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import { muiColors } from "../../Config/Colors";

/**
 * DateField — panelin TEK tarih seçici kabuğu (atom).
 *
 * Daha önce 7 ayrı dosya kendi `datePickerSx`'ini tanımlıyordu ve yükseklik
 * (40/42/48px), köşe yarıçapı, hover/focus renkleri her yerde biraz farklıydı.
 * Burada tek tanım var; ölçü farkı gerekirse `size` ile alınır.
 *
 * `LocalizationProvider` bilerek İÇERİDE: her çağrı yerinin adapter + tr
 * locale sarmalamasını tekrar kurması gereksizdi ve unutulduğunda tarihler
 * İngilizce basılıyordu. İç içe provider MUI'de sorunsuzdur.
 *
 * @param {string}   [label]        Üst etiket (yoksa etiket satırı çizilmez)
 * @param {boolean}  [required]     Etikete kırmızı yıldız ekler
 * @param {Date|null} value
 * @param {(d:Date|null)=>void} onChange
 * @param {"sm"|"md"|"lg"} [size="md"]  40 / 42 / 48 px yükseklik
 * @param {string[]} [views]        MUI görünümleri (ör. ["year"], ["year","month"])
 * @param {string}   [format]       Görüntü formatı (varsayılan dd/MM/yyyy)
 * @param {boolean}  [disableFuture]
 * @param {Date}     [minDate]
 * @param {Date}     [maxDate]
 * @param {boolean}  [disabled]
 * @param {boolean}  [error]
 * @param {string}   [errorMessage]
 * @param {boolean}  [disablePortal] Drawer/modal içinde popper kırpılıyorsa true
 * @param {string}   [placeholder]   Etiket yerine alan içi ipucu
 * @param {number}   [zIndex=9999]   Popper katmanı — modal içinde yükseltilir
 * @param {string}   [className]     Dış sarmalayıcı
 */

const HEIGHTS = { sm: "40px", md: "42px", lg: "48px" };

export default function DateField({
  label,
  required = false,
  value,
  onChange,
  size = "md",
  views,
  format = "dd/MM/yyyy",
  disableFuture = false,
  minDate,
  maxDate,
  disabled = false,
  error = false,
  errorMessage = "",
  placeholder,
  disablePortal = false,
  zIndex = 9999,
  className = "",
  ...rest
}) {
  // Takvim yalnızca küçük ikona değil, alanın herhangi bir yerine tıklayınca
  // açılır. Bu davranış eskiden yalnızca ReportFilterCard'da vardı; ortak
  // bileşene alınınca panelin tamamında tutarlı hale geldi.
  const [open, setOpen] = useState(false);
  const sx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        height: HEIGHTS[size] || HEIGHTS.md,
        fontSize: "14px",
        backgroundColor: muiColors.fieldBg,
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: muiColors.fieldBorderHover,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: muiColors.fieldBorderFocus,
          borderWidth: 2,
        },
      },
      "& .MuiInputBase-input": { padding: "8px 0 8px 12px", fontSize: "14px" },
    }),
    [size]
  );

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1 sm:mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
        <DatePicker
          value={value ?? null}
          onChange={onChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          format={format}
          views={views}
          disableFuture={disableFuture}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          slotProps={{
            popper: disablePortal
              ? { disablePortal: true, sx: { zIndex } }
              : { sx: { zIndex } },
            desktopPaper: { sx: { zIndex } },
            dialog: { sx: { zIndex } },
            textField: {
              fullWidth: true,
              size: "small",
              error,
              helperText: error ? errorMessage : undefined,
              onClick: () => !disabled && setOpen(true),
              placeholder,
              sx,
            },
          }}
          {...rest}
        />
      </LocalizationProvider>
    </div>
  );
}
